import type { Store, StoreConnection, StoreRole } from "@hobbyshopify/backend-client";

export type OnboardingStep = "shop" | "selling" | "show" | "locations" | "website" | "payments";

/** How the business sells: at other shops' shows from a booth, or from premises of its own. */
export type SellingPath = "shows" | "shop";

// Website sits before Payments because Stripe consumes its answer: the shop's
// web address is prefilled onto the connected account at the moment that
// account is created, so asking afterwards would be too late for the first
// attempt.
//
// A vendor is not asked: Stripe is told the business sells from a table at
// shows instead, which it accepts in place of a website.
export function onboardingSteps(path: SellingPath | null): { key: OnboardingStep; label: string }[] {
  if (path === "shows") {
    return [
      { key: "shop", label: "Name" },
      { key: "selling", label: "Selling" },
      { key: "show", label: "Show" },
      { key: "payments", label: "Payments" },
    ];
  }
  return [
    { key: "shop", label: "Name" },
    { key: "selling", label: "Selling" },
    { key: "locations", label: "Address" },
    { key: "website", label: "Website" },
    { key: "payments", label: "Payments" },
  ];
}

export const onboardingPath = (step: OnboardingStep, storeId?: string, claimWalkthrough = false) => {
  const params = new URLSearchParams();
  if (storeId) params.set("storeId", storeId);
  if (claimWalkthrough) params.set(step === "shop" ? "claimReview" : "claimOnboarding", "1");
  const query = params.toString();
  return `/onboarding/${step}${query ? `?${query}` : ""}`;
};

export const claimedShopOnboardingPath = (storeId: string) => onboardingPath("shop", storeId, true);

export function stripeConnection(store: Store | null | undefined): StoreConnection | null {
  return store?.connections.find((connection) => connection.providerKey === "stripe") ?? null;
}

// Payments is the one skippable step, and the backend has no column for "asked
// and declined", so the deferral is recorded locally.
//
// It excuses **payments only**. An earlier version let this flag short-circuit
// the whole resolver, which meant a browser that had once finished the wizard
// stopped noticing a shop that had since lost its last location — the merchant
// got the app with nothing behind it. Missing shop or location is read from the
// server every time, so it is answered on whichever device the merchant opens.
//
// The key still says "onboarding-complete": renaming it would re-prompt every
// merchant who has already dismissed the step.
const paymentsDeferredKey = (storeId: string) => `hobbyscan:onboarding-complete:${storeId}`;

// Website has no deferral: unlike payments it must be answered.
//
// The consequence is deliberate and worth knowing. `resolveOnboardingStep`
// re-answers every aspect from the server on each load, so a shop that predates
// this step — and therefore has neither a websiteUrl nor a slug — is sent here
// the next time its merchant opens the app. That is one screen, once, and it is
// the price of every shop having an address Stripe can verify.

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    // Storage denied (private mode). The wizard simply offers itself again.
  }
}

// Recorded locally like the payments deferral: the server only knows what the
// answer led to — a booth or premises — and either one ends the question.
const sellingPathKey = (storeId: string) => `hobbyscan:onboarding-selling:${storeId}`;

export function sellingPath(storeId: string): SellingPath | null {
  try {
    const value = window.localStorage.getItem(sellingPathKey(storeId));
    return value === "shows" || value === "shop" ? value : null;
  } catch {
    return null;
  }
}

export function chooseSellingPath(storeId: string, path: SellingPath): void {
  try {
    window.localStorage.setItem(sellingPathKey(storeId), path);
  } catch {
    // Storage denied: the question is asked again on the next visit.
  }
}

export const isPaymentsDeferred = (storeId: string) => readFlag(paymentsDeferredKey(storeId));
export const deferPayments = (storeId: string) => writeFlag(paymentsDeferredKey(storeId));

/**
 * A shop has answered the website question once it has its own site, or what
 * its hosted page needs before Stripe will take it as the business's: every
 * shop has the page itself, so its handle answers nothing.
 */
export const hasWebAddress = (store: Store | null | undefined): boolean =>
  Boolean(store?.websiteUrl?.trim() || (store?.contactEmail?.trim() && store?.returnsPolicy?.trim()));

/**
 * The aspect of setup that is still missing, as the step that fixes it — or null
 * when the shop is set up and the wizard has no business appearing.
 *
 * Each aspect is checked on its own, in wizard order, against what the server
 * says: a shop with a name, somewhere to trade from, and a way to be paid. The
 * order is also the resume order, so closing the tab after step 1 brings you
 * back to step 2 rather than to step 1 or to the dashboard — and losing an
 * aspect later sends you to *that* step, not through the wizard from the top.
 *
 * `role` is the caller's role in `store` (`null` while there is no store yet,
 * which is also every brand-new account). Every step here — `PATCH` on the
 * store, its locations, and Stripe — is Owner-only on the backend, so a
 * Manager or Staff member of an existing shop is never sent to the wizard:
 * they could not finish it, only stall on a step with no working action.
 */
export function resolveOnboardingStep({
  store,
  locationCount,
  boothsOnly = false,
  role,
}: {
  store: Store | null;
  locationCount: number;
  /** Every location is a booth at a show: a vendor, read from the server rather than the stored answer. */
  boothsOnly?: boolean;
  role: StoreRole | null;
}): OnboardingStep | null {
  if (role !== null && role !== "OWNER") return null;
  // No shop, or one with nothing to call it — a nameless shop is what customers
  // would see on the map, and what every screen in the app puts in its header.
  if (!store?.name?.trim()) return "shop";
  // Somewhere to trade from: a booth at a show counts as much as premises, so
  // a vendor whose table is already on the shop is never asked either.
  if (locationCount === 0) {
    const path = sellingPath(store.id);
    return path === "shows" ? "show" : path === "shop" ? "locations" : "selling";
  }
  // Asked before payments, because Stripe reads the answer when it creates the
  // account. Required: an account with no verifiable web address stalls in
  // Stripe's review, and there is a working answer for every shop — their own
  // site, or a page we host.
  if (!boothsOnly && !hasWebAddress(store)) return "website";
  // Taking payments is the finished state, and it is server truth: it holds on a
  // new device, a cleared browser, and for accounts that predate the wizard.
  if (stripeConnection(store)?.status === "ACTIVE") return null;
  // Not connected, but the merchant has already been asked and said later. The
  // dashboard's `SetupChecklist` keeps offering it; the wizard stops.
  if (isPaymentsDeferred(store.id)) return null;
  return "payments";
}

// Stripe's hosted onboarding returns through the backend, which redirects to the
// store settings page — it has no idea the wizard sent the user there. The
// wizard leaves this breadcrumb before handing off so the gate can bounce that
// redirect back into step 3, where the three return states are explained.
const STRIPE_RETURN_KEY = "hobbyscan:onboarding-stripe-return";

export function markStripeReturnsToWizard(): void {
  try {
    window.sessionStorage.setItem(STRIPE_RETURN_KEY, "1");
  } catch {
    // Without the breadcrumb the user lands on Settings, which shows the same
    // status — a worse ending, not a broken one.
  }
}

export function consumeStripeReturnToWizard(): boolean {
  try {
    const pending = window.sessionStorage.getItem(STRIPE_RETURN_KEY) === "1";
    if (pending) window.sessionStorage.removeItem(STRIPE_RETURN_KEY);
    return pending;
  } catch {
    return false;
  }
}
